// src/components/Products/ProductCard.js
import React from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { resolveImageUrls, resolveCredit, buildAssetUrl, retryThenPlaceholder } from '../../utils/imageResolver';
import { createSpeciesSlug } from '../../utils/slugUtils';
import { isAssortedBox } from '../../utils/packages';
import ImageAttribution from '../media/ImageAttribution';
import PhotoCarousel from '../common/PhotoCarousel';
import { BookOpen } from 'lucide-react';
import { NO_PHOTO_SRC } from '../../constants/noPhoto';
// Debug imports removed for production
import '../../styles/products.css';
import { formatScientificName, formatSpeciesName, formatLocality } from '../../utils/scientificName';
import { formatProductName } from '../../utils/productName';

// #99: `eagerImage` — pages pass true for above-the-fold cards so the
// card's primary photo is requested immediately instead of riding native
// lazy-load (the one code path where a visible card can sit with its
// image unrequested; operator-reported intermittent never-loads on
// /frogs). Below-fold cards keep loading="lazy" (verified to fetch on
// scroll).
const ProductCard = ({ product, hideCareGuideLink = false, compact = false, hideImage = false, eagerImage = false }) => {
    // Species card image resolution
    const sImg = resolveImageUrls(product.species);
    const src = buildAssetUrl(sImg?.url);
    // Attribution for the displayed image (null when it's a facility
    // upload, in which case ImageAttribution renders nothing).
    const credit = resolveCredit(product.species);
    // S2-01.3: product gallery (from list API) → manual card carousel.
    // Card-size images prefer the 400px thumb rendition (thumb_url falls
    // back to the original server-side).
    const cardGallery = (Array.isArray(product?.gallery) && product.gallery.length > 0)
      ? product.gallery.map((g) => ({
          url: buildAssetUrl(g?.thumb_url || g?.url),
          thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
          credit: (g?.credit || '').trim() || null,
          creditInfo: g?.credit_info || null,  // #110e — license pill fallback
        })).filter((g) => g.url)
      : [];
    // Punch #13: badge when ANY variation carries an active promo.
    const hasPromo = Array.isArray(product?.variations)
      && product.variations.some((v) => v?.promo_display?.has_active_promo);
    // I-03 S3-01.5 — a plant PACKAGE is an ordinary product card with two
    // extras: a badge saying it's a box, and the line saying what's in it
    // ("6 cuttings: Pothos ×2, Philodendron ×2, Scindapsus ×2"). The
    // server composes that line (one unit vocabulary, one place); the
    // card just prints it. Absent on every non-package payload.
    const isPackage = Boolean(product?.is_package);
    // I-03 S3-01.7 — plant group label; '' for animals and packages.
    const plantGroup = (product?.plant_group || '').trim();
    const packageSummary = (product?.package_summary || '').trim();
    // I-03 S3-01.6 — an ASSORTED box ("6 assorted cuttings — our
    // selection") is anchored to a species for bookkeeping only. It must
    // show its OWN cover photo or a PLACEHOLDER: the anchor's photo would
    // promise a species the box does not promise. Same for its name, its
    // locality and its care-guide link.
    const isAssorted = isAssortedBox(product);
    const assortedCover = isAssorted
      ? (cardGallery[0]?.url
        || buildAssetUrl(product?.cover_display_url || product?.cover_thumb_url)
        || null)
      : null;
    // Optional, off by default: the eligible species that have stock.
    const mayInclude = isAssorted
      ? (product?.package_components || [])
        .map((c) => c?.display_name)
        .filter(Boolean)
      : [];

    // Debug log removed for production

    // Debug warning removed for production

  // Flattened fields from backend with robust fallback chain
  // Priority: product.name -> species scientific name -> fallback.
  // The TITLE displays through formatProductName (operator ruling
  // 2026-10-10): its leading binomial in proper form, the rest as typed.
  const displayName = formatProductName(product);
  const getScientificName = () => {
    if (displayName) return displayName;
    if (product.species?.genus && product.species?.species) {
      return formatSpeciesName(product.species, { withLocality: false });
    }
    // Last resort - should never reach here with proper backend data
    return 'Species Name Unavailable';
  };

  const scientificName = getScientificName();
  const genus = product.genus || product.species?.genus || '';
  // The italic SUBTITLE is the species' scientific name in proper form
  // (operator ruling 2026-10-10): 'Dendrobates tinctorius "Azureus"'. It
  // used to print the product NAME there, which is the operator's own
  // text ("Dendrobates Tinctorius") and stays his title wherever a
  // product is named; the scientific line is built from the species.
  const scientificSubtitle = (product.species?.genus && product.species?.species)
    ? formatScientificName(product.species.genus, product.species.species, product.locality,
      product.species.species_type)
    : `${scientificName} ${formatLocality(product.locality, product.species?.species_type)}`.trim();

  // Common name fallback: first common name -> scientific name (never "Unnamed")
  const getCommonName = () => {
    // S3-01.6 (operator ruling 2026-09-05): EVERY package goes by its own
    // name. `common_names` are the ANCHOR species' — one component of a
    // curated combo, or nothing at all for an assorted box — so a box
    // titled "Golden Pothos" was naming a plant it never promised. The
    // server blanks them too; this is the client half of the same rule.
    if (isPackage) return displayName || scientificName;
    if (product.common_names?.[0]) return product.common_names[0];
    return scientificName; // Fall back to scientific name, not "Unnamed Frog"
  };

  const commonName = getCommonName();
  // I-02b: individual-mode products show the min–max across listed
  // animals (operator: ranges when animals price differently).
  const priceRange = product.price_range || null;
  const fromPrice = product.price ? `$${parseFloat(product.price).toFixed(2)}` : 'N/A';
  // Cowork re-check #5 — the "was" price struck the DISCOUNTED number:
  // `product.price` is the minimum EFFECTIVE price (already the promo
  // price on a discounted winner), so the card rendered
  // "SALE ~~From $5.40~~ From $5.40". summarize_card_pricing emits the
  // pre-discount figure as `original_price`; that is what gets struck.
  // Older payloads without the field simply show no strike-through
  // rather than a false one.
  const wasPrice = product.original_price
    ? `$${parseFloat(product.original_price).toFixed(2)}`
    : null;

  // Check if species has care guide content
  const hasCareGuideContent = () => {
    const species = product.species;
    if (!species) {
      return false;
    }

    // #132e: the API now sends care_guide_available (same predicate the
    // guide page's not-found state uses) — no more dead-end links from
    // grid cards. Treat a missing field (stale cache / older payloads)
    // as available so the link never vanishes on data we can't judge.
    return species.care_guide_available !== false;
  };

  // Create canonical care guide slug using centralized utility
  const getCareGuideSlug = () => {
    const species = product.species;
    if (!species) return '';

    // Use API-provided canonical slug if available, otherwise generate
    if (species.care_guide_slug) {
      return species.care_guide_slug;
    }

    // #52 smell pass 2026-08-12: was utils/slug.js's speciesCareGuideSlug,
    // a parallel implementation that diverged on apostrophes (obrien vs
    // o-brien) — links from this card could 404 against routes built with
    // the sitemap-contract slug. One slug implementation now.
    return createSpeciesSlug(species);
  };

  // Debug log removed for production

  // Batch 34: a card with no photo shows the neutral no-photo tile
  // (constants/noPhoto.js), drawn whole by `[data-no-photo]` in products.css.
  const cardImageSrc = isAssorted
    ? assortedCover
    : ((cardGallery[0]?.url) || src);

  return (
    <Card className={`product-card h-100 ${compact ? 'mb-3' : 'mb-4'}`}>
      {!hideImage && (
        <div style={{ position: 'relative' }}>
          {cardGallery.length > 1 && !isAssorted ? (
            /* S2-01.3: manual-only carousel on category cards (per-slide
               credits render inside the carousel) */
            <PhotoCarousel images={cardGallery} alt={commonName} hoverAdvance imgClassName="product-image" eagerFirst={eagerImage} />
          ) : (
          <Card.Img
            variant="top"
            src={cardImageSrc || NO_PHOTO_SRC}
            data-no-photo={cardImageSrc ? undefined : '1'}
            alt={isAssorted
              ? (displayName || 'Assorted plant box')
              : formatSpeciesName(product.species, { withLocality: false })}
            className="product-image"
            loading={eagerImage ? 'eager' : 'lazy'}
            onError={retryThenPlaceholder}
          />
          )}
          {/* Punch #13: promo indicator — shoppers click in for details */}
          {hasPromo && (
            <span className="ff-badge product-card-promo-badge">Promo</span>
          )}
          {/* S3-01.5: a box of plants reads as one item on the shelf —
              say so before the shopper counts photos. */}
          {isPackage && (
            <span
              className={`ff-badge product-card-package-badge${
                product.sold_out ? ' product-card-package-badge--stacked' : ''
              }`}
            >
              Package
            </span>
          )}
          {/* #133b: the from-price fallback (#132a) shows a real price on
              fully sold-out products — badge the state so the card is
              honest at a glance (lot-aware server-side). */}
          {product.sold_out && (
            <span className="ff-badge product-card-soldout-badge">Sold Out</span>
          )}
          {/* Bottom-left credit pill when the image is a CC-licensed
              external photo (iNat / Smithsonian). Renders nothing for
              facility uploads. CC-BY attribution requirement. */}
          {credit && !isAssorted && cardGallery.length <= 1 && (
            <div style={{ position: 'absolute', left: 6, bottom: 6, maxWidth: 'calc(100% - 12px)' }}>
              <ImageAttribution asset={credit} variant="pill" />
            </div>
          )}
        </div>
      )}
      <Card.Body className={compact ? 'py-2' : ''}>
        <Card.Title className="product-title">{commonName}</Card.Title>
        {/* A package's subtitle would repeat its own name (title) or —
            worse — print the anchor species' locality. The summary line
            below says what the box is. */}
        {!isPackage && (
          <Card.Subtitle className="mb-2 product-subtitle">
            <em>{scientificSubtitle}</em>
          </Card.Subtitle>
        )}
        {/* I-03 S3-01.7 — the plant GROUP chip (Vining, Fern...). Only
            single-species plant lines carry one: a box draws from a pool
            and is not IN a group, so the server sends '' for packages. */}
        {plantGroup && (
          <span className="ff-badge product-card-group-chip mb-2">
            {plantGroup}
          </span>
        )}
        {isPackage && packageSummary && (
          <Card.Text className="product-package-contents mb-2">
            {packageSummary}
          </Card.Text>
        )}
        {isAssorted && mayInclude.length > 0 && (
          <Card.Text className="product-package-contents mb-2">
            {`May include: ${mayInclude.join(', ')}`}
          </Card.Text>
        )}
        <Card.Text className="product-price">
          {product.has_promo && product.promo_price ? (
            <>
              <span className="price-badge-sale">Sale</span>{' '}
              {wasPrice && (
                <>
                  <span className="price-original">From {wasPrice}</span>{' '}
                </>
              )}
              <span className="price-promo">From ${parseFloat(product.promo_price).toFixed(2)}</span>
            </>
          ) : product.has_promo && product.promo_labels?.length > 0 ? (
            <>
              From {fromPrice}{' '}
              {product.promo_labels.map((lbl, i) => (
                <span key={i} className="price-badge-sale ms-1">{lbl}</span>
              ))}
            </>
          ) : priceRange && priceRange.min !== priceRange.max ? (
            /* I-02b — individual-mode range across listed animals */
            <>${parseFloat(priceRange.min).toFixed(2)} – ${parseFloat(priceRange.max).toFixed(2)}</>
          ) : priceRange ? (
            <>${parseFloat(priceRange.min).toFixed(2)}</>
          ) : (
            <>From {fromPrice}</>
          )}
        </Card.Text>
                {/* Genus pages exist for frogs (0) and geckos (3) only;
                    other types hide the link rather than send a plant
                    into the frogs URL space. Payloads without a
                    species_type field default to frog — the pre-existing
                    behavior. Product detail below stays /frogs/products/
                    — the only detail route today. */}
                {!compact && genus && ((product.species_type ?? product.species?.species_type ?? 0) === 0
                  || (product.species_type ?? product.species?.species_type) === 3) && (
        <Card.Text className="product-genus">
            <Link to={`${(product.species_type ?? product.species?.species_type) === 3 ? '/geckos' : '/frogs'}/${genus.toLowerCase()}`} className="genus-link">
            More from {genus}
            </Link>
        </Card.Text>
        )}
        {/* S3-01.6: an assorted box's care guide would be its ANCHOR
            species' guide — a species the shopper was never promised. */}
        {!hideCareGuideLink && !isAssorted && hasCareGuideContent() && (
          <Card.Text className="product-care-guide mb-2">
            <Link to={`/care-guides/${getCareGuideSlug()}`} className="care-guide-link">
              <BookOpen size={14} className="ff-link-icon" aria-hidden="true" focusable="false" />
              Care Guide
            </Link>
          </Card.Text>
        )}
        {/* Design System v1 §2.2 (Phase 3): View Details is the card's
            ordinary action — a SECONDARY. The one primary on a storefront
            page is Add to Cart, and it lives on the product page. */}
        <Link
          to={`/frogs/products/${product.id}`}
          className={`ff-btn ff-btn--secondary${compact ? ' ff-btn--sm' : ''} product-card-cta`}
        >
          View Details
        </Link>
      </Card.Body>
    </Card>
  );
};

export default ProductCard;
